// TimerContext
//
// A single global stopwatch for billable time. Lives at the app root so
// lawyers can start a timer on the Matter Detail page, navigate around,
// and stop it from the floating widget without losing the elapsed time.
//
// State shape:
//   { running, startedAt, matterId, description }
//
//   running       — boolean; whether the timer is currently counting
//   startedAt     — ms epoch when start was pressed (or restored from storage)
//   matterId      — optional, the matter the entry will be saved against
//   description   — optional, free text
//
// Elapsed time is derived from (Date.now() - startedAt). We deliberately
// don't store `elapsed` — drift across refreshes is impossible this way.
//
// Persistence: the (startedAt, matterId, description) tuple is written
// to localStorage on every change so a browser refresh, crash, or
// connection drop doesn't lose the running timer. Only ONE timer can be
// active per browser; that matches user mental model (you're working on
// one thing at a time).

import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';

const STORAGE_KEY = 'lf_global_timer_v1';

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.startedAt) return null;
    return parsed;
  } catch { return null; }
}

function saveToStorage(state) {
  try {
    if (state?.running && state.startedAt) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        startedAt: state.startedAt,
        matterId:  state.matterId,
        description: state.description,
      }));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch { /* quota / private — ignore */ }
}

const TimerContext = createContext(null);

export function TimerProvider({ children }) {
  const [state, setState] = useState(() => {
    const restored = loadFromStorage();
    if (restored) {
      return {
        running:     true,
        startedAt:   restored.startedAt,
        matterId:    restored.matterId || '',
        description: restored.description || '',
      };
    }
    return { running: false, startedAt: null, matterId: '', description: '' };
  });

  // Tick once per second when running so subscribers can re-render.
  // We store a `now` value rather than `elapsed` so consumers compute
  // elapsed themselves from (now - startedAt) — no drift on refresh.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!state.running) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [state.running]);

  // Persist whenever the running state changes.
  useEffect(() => { saveToStorage(state); }, [state]);

  const startTimer = useCallback((opts = {}) => {
    setState({
      running:     true,
      startedAt:   Date.now(),
      matterId:    opts.matterId    || '',
      description: opts.description || '',
    });
    setNow(Date.now());
  }, []);

  const stopTimer = useCallback(() => {
    setState((s) => ({ ...s, running: false }));
  }, []);

  const resetTimer = useCallback(() => {
    setState({ running: false, startedAt: null, matterId: '', description: '' });
  }, []);

  // Lets the floating widget edit matter/description while running.
  const updateTimerMeta = useCallback((patch) => {
    setState((s) => ({ ...s, ...patch }));
  }, []);

  const elapsedMs = state.running && state.startedAt ? Math.max(0, now - state.startedAt) : 0;
  // Round-to-six-minute (1/10 hour) is the standard billable increment.
  const elapsedMinutes = Math.max(0, Math.round(elapsedMs / 60000));
  const billableMinutes = Math.max(6, elapsedMinutes); // minimum 6-minute bill if started

  const value = useMemo(() => ({
    running:        state.running,
    startedAt:      state.startedAt,
    matterId:       state.matterId,
    description:    state.description,
    elapsedMs,
    elapsedMinutes,
    billableMinutes,
    startTimer,
    stopTimer,
    resetTimer,
    updateTimerMeta,
  }), [state, elapsedMs, elapsedMinutes, billableMinutes, startTimer, stopTimer, resetTimer, updateTimerMeta]);

  return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>;
}

export const useTimer = () => useContext(TimerContext) || {
  running: false, startedAt: null, matterId: '', description: '',
  elapsedMs: 0, elapsedMinutes: 0, billableMinutes: 6,
  startTimer: () => {}, stopTimer: () => {}, resetTimer: () => {}, updateTimerMeta: () => {},
};
